import React, { useContext, useState, useEffect } from "react";
import Select from "react-select";
import { FaHome } from "react-icons/fa";
import appContext from "../../../../context/app/appContext";

const LocationList = ({ data, onChange }) => {
  const { serverPost } = useContext(appContext);
  const [locationStates, setLocationStates] = useState([]);

  const [debounceTimers, setDebounceTimers] = useState({});

  useEffect(() => {
    setLocationStates(
      data.locations.map(() => ({
        query: "",
        suggestions: [],
        loading: false,
        error: false,
        manualInput: { address: "" },
      }))
    );
    // eslint-disable-next-line
  }, [data.locations.length]);

  const classifyLocation = (address) => {
    const addressLower = address.toLowerCase();
    const isAustralian = addressLower.includes("australia");
    const isQLD =
      addressLower.includes("qld") || addressLower.includes("queensland");
    const isDefaultAddress =
      addressLower.includes("redeemer lutheran college") && isQLD;

    return {
      offSite: !isDefaultAddress,
      interState: isAustralian && !isQLD,
      overSeas: !isAustralian,
    };
  };
  const handleSearchInput = (index, inputValue) => {
    try {
      if (typeof inputValue !== "string") return;

      const updated = [...locationStates];
      updated[index].query = inputValue;
      updated[index].error = false;

      // Cancel previous debounce timer immediately
      if (debounceTimers[index]) {
        clearTimeout(debounceTimers[index]);
      }

      // If input is empty or less than 3 characters, reset suggestions and exit
      if (!inputValue || inputValue.trim().length < 3) {
        updated[index].suggestions = [];
        updated[index].loading = false;
        setLocationStates(updated);

        // Clear stored timer
        setDebounceTimers((prev) => {
          const newTimers = { ...prev };
          delete newTimers[index];
          return newTimers;
        });

        return;
      }

      // Set loading state
      updated[index].loading = true;
      setLocationStates(updated);

      const timer = setTimeout(async () => {
        try {
          const res = await serverPost(`/tours/autoCompleteAddress`, {
            address: inputValue.trim(),
          });

          updated[index].suggestions = res.addressOptions || [];
          updated[index].error = (res.addressOptions || []).length === 0;
        } catch (err) {
          console.error("Address lookup failed:", err);
          updated[index].suggestions = [];
          updated[index].error = true;
        } finally {
          updated[index].loading = false;
          setLocationStates([...updated]);
        }
      }, 400);

      setDebounceTimers((prev) => ({ ...prev, [index]: timer }));
    } catch (error) {
      console.log(error);
    }
    // Ensure it's a valid string
  };
  const handleSelectSuggestion = (index, selectedOption) => {
    if (!selectedOption || !selectedOption.label) {
      return;
    }

    const classified = classifyLocation(selectedOption.label);
    const updatedLocations = [...data.locations];
    updatedLocations[index] = {
      address: selectedOption.label,
      mode: "search",
      ...classified,
    };
    onChange("locations", updatedLocations);

    const updatedState = [...locationStates];
    updatedState[index].query = "";
    updatedState[index].suggestions = [];
    setLocationStates(updatedState);
  };

  const handleModeToggle = (index, mode) => {
    const updatedLocations = [...data.locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      mode,
      address: "",
      ...(mode === "manual" ? { offSite: true } : {}),
    };
    onChange("locations", updatedLocations);
  };

  const handleManualInputChange = (index, value) => {
    const updated = [...locationStates];
    updated[index].manualInput.address = value;
    setLocationStates(updated);
  };

  const handleManualSubmit = (index) => {
    const manual = locationStates[index].manualInput;
    const address = manual.address?.trim();
    if (!address) return alert("Please enter the full manual address.");

    const updatedLocations = [...data.locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      address,
      mode: "manual",
      offSite: data.locations[index].offSite || true,
      interState: data.locations[index].interState || false,
      overSeas: data.locations[index].overSeas || false,
    };
    onChange("locations", updatedLocations);
  };

  const toggleManualFlag = (index, field) => {
    const updatedLocations = [...data.locations];
    updatedLocations[index][field] = !updatedLocations[index][field];
    onChange("locations", updatedLocations);
  };

  const removeLocation = (index) => {
    const updatedLocations = [...data.locations];
    updatedLocations.splice(index, 1);
    onChange("locations", updatedLocations);

    const updatedStates = [...locationStates];
    updatedStates.splice(index, 1);
    setLocationStates(updatedStates);
  };

  const addNewLocation = () => {
    onChange("locations", [
      ...data.locations,
      {
        address: "",
        offSite: true,
        interState: false,
        overSeas: false,
        mode: "search",
      },
    ]);
    setLocationStates([
      ...locationStates,
      {
        query: "",
        suggestions: [],
        loading: false,
        error: false,
        manualInput: { address: "" },
      },
    ]);
  };

  const hasHomeAddress = data.locations.some(
    (loc) =>
      loc.address?.toLowerCase().includes("redeemer lutheran college") &&
      loc.address.toLowerCase().includes("qld")
  );

  return (
    <div className="space-y-3">
      <label className="block font-medium text-gray-700">Locations</label>

      {data.locations.map((location, index) => (
        <div key={index} className="border rounded-md p-4 space-y-3 bg-gray-50">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-700">
              Address {index + 1}
            </h4>
            {data.locations.length > 1 && (
              <button
                onClick={() => removeLocation(index)}
                className="text-sm text-red-500 hover:underline"
              >
                Remove
              </button>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex gap-4 text-sm">
            <label>
              <input
                type="radio"
                checked={location.mode === "search"}
                onChange={() => handleModeToggle(index, "search")}
              />
              <span className="ml-1">Search</span>
            </label>
            <label>
              <input
                type="radio"
                checked={location.mode === "manual"}
                onChange={() => handleModeToggle(index, "manual")}
              />
              <span className="ml-1">Manual</span>
            </label>
          </div>

          {/* Search Mode */}
          {location.mode === "search" ? (
            !location.address ? (
              <Select
                onInputChange={(newValue) => {
                  handleSearchInput(index, newValue);
                  return newValue;
                }}
                onChange={(option) => handleSelectSuggestion(index, option)}
                options={locationStates[index].suggestions.map((s) => ({
                  value: s.description,
                  label: s.description,
                  placeId: s.placeId,
                }))}
                isLoading={locationStates[index].loading}
                isClearable
                isSearchable
                placeholder="Start typing address..."
              />
            ) : (
              <>
                <p className="text-sm mt-2 text-gray-700 flex items-center gap-2">
                  Selected:
                  <strong className="ml-1">{location.address}</strong>
                  {location.address
                    .toLowerCase()
                    .includes("redeemer lutheran college") &&
                    location.address.toLowerCase().includes("qld") && (
                      <span title="Default address" className="text-blue-500">
                        <FaHome size={20} />
                      </span>
                    )}
                </p>
                <button
                  onClick={() => {
                    const updated = [...data.locations];
                    updated[index].address = "";
                    onChange("locations", updated);
                  }}
                  className="text-sm text-blue-600 hover:underline mt-1"
                >
                  Change address
                </button>
              </>
            )
          ) : !location.address ? (
            <>
              <textarea
                placeholder="Enter location details..."
                value={locationStates[index].manualInput.address}
                onChange={(e) => handleManualInputChange(index, e.target.value)}
                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded-md"
              />
              <div className="flex gap-6 mt-2 text-sm text-gray-700">
                {["offSite", "interState", "overSeas"].map((flag) => (
                  <label key={flag} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={location[flag] || false}
                      onChange={() => toggleManualFlag(index, flag)}
                    />
                    {flag[0].toUpperCase() + flag.slice(1)}
                  </label>
                ))}
              </div>
              <button
                onClick={() => handleManualSubmit(index)}
                disabled={!locationStates[index].manualInput.address.trim()}
                className={`mt-2 px-4 py-2 rounded-md shadow text-white ${
                  locationStates[index].manualInput.address.trim()
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-gray-300 cursor-not-allowed"
                }`}
              >
                Save Address Information
              </button>
            </>
          ) : (
            <>
              <p className="text-sm mt-2 text-gray-700">
                Saved: <strong>{location.address}</strong>
              </p>
              <button
                onClick={() => {
                  const updated = [...data.locations];
                  updated[index].address = "";
                  onChange("locations", updated);
                }}
                className="text-sm text-blue-600 hover:underline mt-1"
              >
                Change address
              </button>
            </>
          )}

          {/* Location tags */}
          {location.address && (
            <div className="text-xs text-gray-500 mt-1">
              {location.offSite && "Off-site"}
              {location.interState && ", Interstate"}
              {location.overSeas && ", Overseas"}
            </div>
          )}
        </div>
      ))}
      {hasHomeAddress && (
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rooms / Resources required
          </label>
          <textarea
            rows={3}
            className="w-full border rounded-md px-3 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="List college venues/resources required for event..."
            value={data.roomResource || ""}
            onChange={(e) => onChange("roomResource", e.target.value)}
          />
          {hasHomeAddress && !data.roomResource.trim() && (
            <p className="text-sm text-red-600 mt-1">
              Please specify rooms/resources required for events on campus.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={addNewLocation}
          disabled={!data.locations.some((loc) => loc.address?.trim())}
          className={`py-2 px-3 rounded-md shadow duration-300 ${
            data.locations.some((loc) => loc.address?.trim())
              ? "bg-emerald-400 text-white hover:bg-emerald-600"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          + Add additional address
        </button>
      </div>
    </div>
  );
};

export default LocationList;
